import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { DEFAULT_PORTFOLIO_ASSETS } from '../../constants/ai-trading';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  ScatterChart,
  Scatter
} from 'recharts';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Zap,
  BarChart3,
  RefreshCw,
  Settings,
  Calculator,
  ArrowUpDown,
  DollarSign,
  PieChart as PieChartIcon,
  Activity
} from 'lucide-react';
import { getAITradingService } from '../../services/AITradingService';
import { Asset, RebalanceRecommendation, YieldOpportunity } from '../../types/ai-trading';

interface OptimizationStrategy {
  id: string;
  name: string;
  description: string;
  riskLevel: 'conservative' | 'moderate' | 'aggressive';
  expectedReturn: number;
  maxDrawdown: number;
  sharpeRatio: number;
}

interface PortfolioAllocation {
  current: Record<string, number>;
  optimized: Record<string, number>;
  improvement: {
    returnIncrease: number;
    riskReduction: number;
    sharpeImprovement: number;
  };
}

interface RebalanceSettings {
  riskTolerance: number;
  returnTarget: number;
  autoRebalance: boolean;
  rebalanceThreshold: number;
  maxPositionSize: number;
  minPositionSize: number;
  includeYieldFarming: boolean;
  includeStaking: boolean;
}

export function PortfolioOptimizerAI() {
  const [currentAssets, setCurrentAssets] = useState<Asset[]>([
    ...DEFAULT_PORTFOLIO_ASSETS,
    {
      symbol: 'UNI',
      name: 'Uniswap',
      balance: 67.89,
      usdValue: 567.89,
      change24h: 3.45,
      allocation: 2.8,
      chain: 'ethereum',
      type: 'token'
    },
    {
      symbol: 'AAVE',
      name: 'Aave',
      balance: 12.34,
      usdValue: 1234.56,
      change24h: 0.98,
      allocation: 2.8,
      chain: 'ethereum',
      type: 'token'
    }
  ]);

  const [optimizationResult, setOptimizationResult] = useState<RebalanceRecommendation | null>(null);
  const [yieldOpportunities, setYieldOpportunities] = useState<YieldOpportunity[]>([]);
  const [settings, setSettings] = useState<RebalanceSettings>({
    riskTolerance: 60,
    returnTarget: 15,
    autoRebalance: false,
    rebalanceThreshold: 5,
    maxPositionSize: 40,
    minPositionSize: 2,
    includeYieldFarming: true,
    includeStaking: true
  });

  const [strategies, setStrategies] = useState<OptimizationStrategy[]>([
    {
      id: 'conservative',
      name: 'Conservative Growth',
      description: 'Focus on capital preservation with moderate growth',
      riskLevel: 'conservative',
      expectedReturn: 8.5,
      maxDrawdown: 12,
      sharpeRatio: 1.2
    },
    {
      id: 'balanced',
      name: 'Balanced Portfolio',
      description: 'Optimal risk-reward balance with diversification',
      riskLevel: 'moderate',
      expectedReturn: 15.2,
      maxDrawdown: 18,
      sharpeRatio: 1.6
    },
    {
      id: 'aggressive',
      name: 'Growth Maximization',
      description: 'High growth potential with increased volatility',
      riskLevel: 'aggressive',
      expectedReturn: 28.7,
      maxDrawdown: 35,
      sharpeRatio: 1.4
    }
  ]);

  const [selectedStrategy, setSelectedStrategy] = useState('balanced');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadYieldOpportunities();
  }, []);

  const loadYieldOpportunities = () => {
    const opportunities: YieldOpportunity[] = [
      {
        protocol: 'Uniswap V3',
        pool: 'ETH/USDC',
        apy: 24.5,
        tvl: 125000000,
        risk: 35,
        impermanentLoss: 15.2,
        tokens: ['ETH', 'USDC'],
        chain: 'ethereum',
        category: 'defi'
      },
      {
        protocol: 'Aave',
        pool: 'ETH Lending',
        apy: 4.8,
        tvl: 890000000,
        risk: 20,
        impermanentLoss: 0,
        tokens: ['ETH'],
        chain: 'ethereum',
        category: 'lending'
      },
      {
        protocol: 'Ethereum 2.0',
        pool: 'ETH Staking',
        apy: 5.2,
        tvl: 45000000000,
        risk: 15,
        impermanentLoss: 0,
        tokens: ['ETH'],
        chain: 'ethereum',
        category: 'staking'
      },
      {
        protocol: 'Curve',
        pool: 'stETH/ETH',
        apy: 8.9,
        tvl: 567000000,
        risk: 25,
        impermanentLoss: 5.8,
        tokens: ['stETH', 'ETH'],
        chain: 'ethereum',
        category: 'defi'
      },
      {
        protocol: 'Compound',
        pool: 'USDC Lending',
        apy: 3.2,
        tvl: 1200000000,
        risk: 18,
        impermanentLoss: 0,
        tokens: ['USDC'],
        chain: 'ethereum',
        category: 'lending'
      }
    ];

    setYieldOpportunities(opportunities);
  };

  const optimizePortfolio = async () => {
    try {
      setLoading(true);
      
      const optimization = await getAITradingService().optimizePortfolio(currentAssets);
      setOptimizationResult(optimization);
      
    } catch (error) {
      console.error('Error optimizing portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const executeRebalance = async () => {
    if (!optimizationResult) return;

    try {
      // In a real implementation, this would execute the trades
      console.log('Executing rebalance:', optimizationResult.actions);
      
      // Update current allocations to optimized ones
      const totalValue = currentAssets.reduce((sum, asset) => sum + asset.usdValue, 0);
      
      const updatedAssets = currentAssets.map(asset => {
        const newAllocation = optimizationResult.recommendedAllocation[asset.symbol] || 0;
        const newUsdValue = (newAllocation / 100) * totalValue;
        
        return {
          ...asset,
          allocation: newAllocation,
          usdValue: newUsdValue,
          balance: newUsdValue / (asset.usdValue / asset.balance)
        };
      });

      setCurrentAssets(updatedAssets);
      setOptimizationResult(null);
      
    } catch (error) {
      console.error('Error executing rebalance:', error);
    }
  };

  const getStrategyColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'conservative': return 'text-green-500';
      case 'moderate': return 'text-yellow-500';
      case 'aggressive': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getRiskColor = (risk: number) => {
    if (risk < 25) return '#22c55e';
    if (risk < 50) return '#eab308';
    if (risk < 75) return '#f97316';
    return '#ef4444';
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'staking': return '#8b5cf6';
      case 'lending': return '#06b6d4';
      case 'defi': return '#f59e0b';
      case 'farming': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Chart data
  const currentAllocationData = currentAssets.map(asset => ({
    name: asset.symbol,
    value: asset.allocation,
    fill: `hsl(${Math.random() * 360}, 70%, 50%)`
  }));

  const optimizedAllocationData = optimizationResult ? 
    Object.entries(optimizationResult.recommendedAllocation).map(([symbol, allocation]) => ({
      name: symbol,
      value: allocation,
      fill: `hsl(${Math.random() * 360}, 70%, 50%)`
    })) : [];

  const riskReturnData = strategies.map(strategy => ({
    name: strategy.name,
    risk: strategy.maxDrawdown,
    return: strategy.expectedReturn,
    sharpe: strategy.sharpeRatio
  }));

  const yieldComparisonData = yieldOpportunities.map(opp => ({
    name: opp.protocol,
    apy: opp.apy,
    risk: opp.risk,
    tvl: opp.tvl / 1000000 // Convert to millions
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
            <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1>AI Portfolio Optimizer</h1>
            <p className="text-muted-foreground">
              Machine learning-powered portfolio optimization and rebalancing
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={selectedStrategy}
            onChange={(e) => setSelectedStrategy(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-background"
          >
            {strategies.map((strategy) => (
              <option key={strategy.id} value={strategy.id}>
                {strategy.name}
              </option>
            ))}
          </select>

          <Button
            onClick={optimizePortfolio}
            disabled={loading}
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Calculator className="h-4 w-4 mr-2" />
            )}
            Optimize Portfolio
          </Button>
        </div>
      </div>

      {/* Portfolio Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold">
                  ${currentAssets.reduce((sum, asset) => sum + asset.usdValue, 0).toLocaleString()}
                </p>
                <p className="text-xs text-green-500">+3.45%</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Assets</p>
                <p className="text-2xl font-bold">{currentAssets.length}</p>
                <p className="text-xs text-muted-foreground">Tokens</p>
              </div>
              <PieChartIcon className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top Allocation</p>
                <p className="text-2xl font-bold">
                  {Math.max(...currentAssets.map(a => a.allocation)).toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">
                  {currentAssets.find(a => a.allocation === Math.max(...currentAssets.map(a => a.allocation)))?.symbol}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Diversification</p>
                <p className="text-2xl font-bold text-green-500">78%</p>
                <p className="text-xs text-muted-foreground">Score</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="current" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="current">Current Portfolio</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
          <TabsTrigger value="yield">Yield Opportunities</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Current Allocation</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={currentAllocationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                    >
                      {currentAllocationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Portfolio Assets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentAssets.map((asset) => (
                    <div
                      key={asset.symbol}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {asset.symbol.slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-semibold">{asset.symbol}</p>
                          <p className="text-sm text-muted-foreground">{asset.name}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="font-semibold">${asset.usdValue.toLocaleString()}</p>
                        <p className={`text-sm ${
                          asset.change24h >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {asset.change24h >= 0 ? '+' : ''}{asset.change24h.toFixed(2)}%
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="font-semibold">{asset.allocation.toFixed(1)}%</p>
                        <Progress value={asset.allocation} className="w-16 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Strategy Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart data={riskReturnData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="risk" 
                    name="Max Drawdown %" 
                    label={{ value: 'Risk (Max Drawdown %)', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis 
                    dataKey="return" 
                    name="Expected Return %" 
                    label={{ value: 'Expected Return %', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value}${name === 'risk' ? '%' : name === 'return' ? '%' : ''}`, 
                      name === 'risk' ? 'Max Drawdown' : name === 'return' ? 'Expected Return' : 'Sharpe Ratio'
                    ]}
                  />
                  <Scatter dataKey="return" fill="#8884d8" />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="optimization" className="space-y-4">
          {optimizationResult ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Expected Return Increase</p>
                      <p className="text-2xl font-bold text-green-500">
                        +{optimizationResult.expectedReturn.toFixed(1)}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Risk Reduction</p>
                      <p className="text-2xl font-bold text-blue-500">
                        -{optimizationResult.riskReduction.toFixed(1)}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Gas Cost</p>
                      <p className="text-2xl font-bold">
                        ${optimizationResult.gasCost}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Current vs Optimized</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(optimizationResult.currentAllocation).map(([asset, current]) => {
                        const optimized = optimizationResult.recommendedAllocation[asset] || 0;
                        const difference = optimized - current;
                        
                        return (
                          <div key={asset} className="space-y-2">
                            <div className="flex justify-between">
                              <span className="font-semibold">{asset}</span>
                              <span className={`text-sm ${
                                difference > 0 ? 'text-green-500' : 
                                difference < 0 ? 'text-red-500' : 'text-gray-500'
                              }`}>
                                {difference > 0 ? '+' : ''}{difference.toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex space-x-2">
                              <div className="flex-1">
                                <div className="text-xs text-muted-foreground mb-1">Current: {current.toFixed(1)}%</div>
                                <Progress value={current} className="h-2" />
                              </div>
                              <div className="flex-1">
                                <div className="text-xs text-muted-foreground mb-1">Optimized: {optimized.toFixed(1)}%</div>
                                <Progress value={optimized} className="h-2" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recommended Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {optimizationResult.actions.map((action, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            {action.type === 'buy' ? (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            )}
                            <div>
                              <p className="font-semibold">
                                {action.type.toUpperCase()} {action.asset}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {action.amount.toFixed(1)}% allocation
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="font-semibold">
                              ${action.usdValue.toLocaleString()}
                            </p>
                            <Badge 
                              variant={action.priority === 1 ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              Priority {action.priority}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <Target className="h-4 w-4" />
                <AlertDescription>
                  <strong>Optimization Analysis:</strong> {optimizationResult.reasoning}
                </AlertDescription>
              </Alert>

              <div className="flex justify-end space-x-4">
                <Button variant="outline" onClick={() => setOptimizationResult(null)}>
                  Cancel
                </Button>
                <Button onClick={executeRebalance}>
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  Execute Rebalance
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Calculator className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Portfolio Optimization</h3>
                <p className="text-muted-foreground mb-4">
                  Click "Optimize Portfolio" to get AI-powered recommendations for improving your allocation.
                </p>
                <Button onClick={optimizePortfolio} disabled={loading}>
                  {loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Calculator className="h-4 w-4 mr-2" />
                  )}
                  Start Optimization
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="yield" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Yield Farming & Staking Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {yieldOpportunities.map((opportunity, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold">{opportunity.protocol}</span>
                          <Badge 
                            style={{ backgroundColor: getCategoryColor(opportunity.category) }}
                            className="text-white"
                          >
                            {opportunity.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{opportunity.pool}</p>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-500">
                          {opportunity.apy.toFixed(1)}% APY
                        </div>
                        <p className="text-sm text-muted-foreground">
                          TVL: ${(opportunity.tvl / 1000000).toFixed(0)}M
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Risk Score</p>
                        <div className="flex items-center space-x-2">
                          <Progress 
                            value={opportunity.risk} 
                            className="flex-1"
                            style={{
                              '--progress-background': getRiskColor(opportunity.risk)
                            } as React.CSSProperties}
                          />
                          <span className="text-sm font-semibold">{opportunity.risk}</span>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-muted-foreground">Impermanent Loss</p>
                        <p className="font-semibold">{opportunity.impermanentLoss.toFixed(1)}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-muted-foreground">Chain</p>
                        <p className="font-semibold capitalize">{opportunity.chain}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2">
                        {opportunity.tokens.map((token, tokenIndex) => (
                          <Badge key={tokenIndex} variant="outline">
                            {token}
                          </Badge>
                        ))}
                      </div>
                      
                      <Button size="sm" variant="outline">
                        <Zap className="h-4 w-4 mr-1" />
                        Add to Portfolio
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Yield vs Risk Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart data={yieldComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="risk" 
                    name="Risk Score" 
                    label={{ value: 'Risk Score', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis 
                    dataKey="apy" 
                    name="APY %" 
                    label={{ value: 'APY %', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value}${name === 'apy' ? '%' : ''}`, 
                      name === 'apy' ? 'APY' : name === 'risk' ? 'Risk Score' : 'TVL (M)'
                    ]}
                    labelFormatter={(label) => `Protocol: ${label}`}
                  />
                  <Scatter dataKey="apy" fill="#22c55e" />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Optimization Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>Risk Tolerance: {settings.riskTolerance}%</Label>
                  <Slider
                    value={[settings.riskTolerance]}
                    onValueChange={([value]) => 
                      setSettings(prev => ({ ...prev, riskTolerance: value }))
                    }
                    max={100}
                    step={5}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Return Target: {settings.returnTarget}%</Label>
                  <Slider
                    value={[settings.returnTarget]}
                    onValueChange={([value]) => 
                      setSettings(prev => ({ ...prev, returnTarget: value }))
                    }
                    min={5}
                    max={50}
                    step={1}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Rebalance Threshold: {settings.rebalanceThreshold}%</Label>
                  <Slider
                    value={[settings.rebalanceThreshold]}
                    onValueChange={([value]) => 
                      setSettings(prev => ({ ...prev, rebalanceThreshold: value }))
                    }
                    min={1}
                    max={20}
                    step={0.5}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Max Position Size: {settings.maxPositionSize}%</Label>
                    <Slider
                      value={[settings.maxPositionSize]}
                      onValueChange={([value]) => 
                        setSettings(prev => ({ ...prev, maxPositionSize: value }))
                      }
                      min={10}
                      max={100}
                      step={5}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>Min Position Size: {settings.minPositionSize}%</Label>
                    <Slider
                      value={[settings.minPositionSize]}
                      onValueChange={([value]) => 
                        setSettings(prev => ({ ...prev, minPositionSize: value }))
                      }
                      min={0.5}
                      max={10}
                      step={0.5}
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Rebalancing</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically rebalance when threshold is reached
                    </p>
                  </div>
                  <Switch
                    checked={settings.autoRebalance}
                    onCheckedChange={(checked) =>
                      setSettings(prev => ({ ...prev, autoRebalance: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Include Yield Farming</Label>
                    <p className="text-sm text-muted-foreground">
                      Consider DeFi yield farming opportunities
                    </p>
                  </div>
                  <Switch
                    checked={settings.includeYieldFarming}
                    onCheckedChange={(checked) =>
                      setSettings(prev => ({ ...prev, includeYieldFarming: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Include Staking</Label>
                    <p className="text-sm text-muted-foreground">
                      Consider staking opportunities
                    </p>
                  </div>
                  <Switch
                    checked={settings.includeStaking}
                    onCheckedChange={(checked) =>
                      setSettings(prev => ({ ...prev, includeStaking: checked }))
                    }
                  />
                </div>
              </div>

              <Button className="w-full">
                <Settings className="h-4 w-4 mr-2" />
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}